<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { useAuth } from '@/lib/useAuth';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import logoUrl from '@/assets/logo.svg';

const { isBootBlocked, bootFailed, bootAttempt, retryBoot, logout } = useAuth();

const isDevelopment = computed(() => import.meta.env.DEV);

const SLOW_BOOT_AFTER_SECONDS = 5;

const elapsedSeconds = ref(0);
let bootTicker: ReturnType<typeof setInterval> | undefined;

const stopTicker = () => {
  if (bootTicker !== undefined) {
    clearInterval(bootTicker);
    bootTicker = undefined;
  }
};

// Tick only while the boot is ACTIVELY syncing (App.vue is the root component
// and never unmounts, so an unconditional interval would run for the whole app
// lifetime). Restarting on each activation also re-baselines the copy: a Retry
// from the failed panel starts back at "Signing you in...".
const bootSyncActive = computed(() => isBootBlocked.value && !bootFailed.value);
watch(
  bootSyncActive,
  (active) => {
    stopTicker();
    if (active) {
      elapsedSeconds.value = 0;
      bootTicker = setInterval(() => {
        elapsedSeconds.value += 1;
      }, 1000);
    }
  },
  { immediate: true },
);

onUnmounted(stopTicker);

const bootMessage = computed(() => {
  if (bootAttempt.value > 1) {
    return 'Connection trouble, retrying...';
  }
  if (elapsedSeconds.value >= SLOW_BOOT_AFTER_SECONDS) {
    return 'Still connecting...';
  }
  return 'Signing you in...';
});
</script>

<template>
  <div v-if="isBootBlocked" class="flex min-h-screen items-center justify-center bg-background">
    <div class="flex flex-col items-center gap-4">
      <img :src="logoUrl" alt="Stoxly" class="h-10 w-10" />
      <template v-if="bootFailed">
        <div class="flex flex-col items-center gap-1 text-center">
          <p class="text-sm font-medium">We couldn't sign you in</p>
          <p class="text-sm text-muted-foreground">Check your connection. Your session is safe.</p>
        </div>
        <div class="flex items-center gap-2">
          <Button size="sm" @click="retryBoot">Retry</Button>
          <Button size="sm" variant="outline" @click="logout">Go to login</Button>
        </div>
      </template>
      <div v-else class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 class="h-4 w-4 animate-spin" />
        {{ bootMessage }}
      </div>
    </div>
  </div>
  <router-view v-else />

  <Toaster theme="dark" position="bottom-right" rich-colors />
  <VueQueryDevtools v-if="isDevelopment" />
</template>
